import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { useAuth } from './AuthContext'

// Route guard for /platform/*.
//
// A user's platform_admin *membership* is what proves they should have
// access to the platform surface — but the JWT projects a single active
// membership at a time, and the backend's RequireRole("platform_admin")
// middleware inspects the JWT's active role. So a platform admin who is
// currently signed into a tenant workspace (e.g. Vijay active as admin
// on acme-water) will see /platform/* return 403 on every request,
// which surfaces as "role required" in the platform pages.
//
// This wrapper closes that gap: if the user holds a platform_admin
// membership but is not currently active on the platform, it triggers
// switchMembership(__platform, platform_admin) before rendering. The
// switch re-mints the JWT with roles=["platform_admin"], and the
// downstream API calls then succeed.
//
// After the switch, the sidebar's own logic hides tenant-scoped
// surfaces (they'd 400 with "zero tenant id" anyway from the platform
// session) — the user can pick a tenant again from the workspace menu
// when they're done.
//
// Non-platform users (no platform_admin membership) are bounced to /.
export default function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const { user, switchMembership } = useAuth()

  const platformMembership = user?.memberships?.find((m) => m.role === 'platform_admin')
  const activeIsPlatform = !!user?.active?.is_platform

  const [switching, setSwitching] = useState(false)
  const [switchError, setSwitchError] = useState<string | null>(null)
  // One-shot guard: only auto-switch on the FIRST render pass under this
  // route. If the user later switches to a tenant via the TopBar menu (the
  // TopBar navigates away to '/', but there's a render frame between the
  // active-flip and the navigate), we must not fight that intentional
  // switch by pulling them back to platform. The ref is set true on the
  // first effect run whether we needed a switch or not, so any subsequent
  // active flip is a no-op here.
  const mounted = useRef(false)

  useEffect(() => {
    if (mounted.current) return
    mounted.current = true
    if (!platformMembership) return
    if (activeIsPlatform) return
    setSwitching(true)
    setSwitchError(null)
    switchMembership(platformMembership.tenant_id, 'platform_admin')
      .catch((e) => setSwitchError(e instanceof Error ? e.message : String(e)))
      .finally(() => setSwitching(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // No platform membership → this user has no business here.
  if (!platformMembership) return <Navigate to="/" replace />

  // Show the "switching" overlay only during the actual first-mount switch
  // OR the very first render (before the useEffect has fired). After we've
  // mounted, a non-platform active means the user intentionally switched
  // to a tenant via the TopBar — that handler is about to navigate away
  // to '/', so render nothing until the route change unmounts us. Never
  // show the spinner in that case, or it flickers as if we're fighting
  // the user's switch.
  if (switching) {
    return <SwitchingOverlay error={switchError} />
  }
  if (!activeIsPlatform) {
    return mounted.current ? null : <SwitchingOverlay error={switchError} />
  }

  return <>{children}</>
}

function SwitchingOverlay({ error }: { error: string | null }) {
  return (
    <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
      <Stack spacing={1.5} sx={{ alignItems: 'center', maxWidth: 420 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          Switching to the Platform workspace…
        </Typography>
        {error && (
          <Alert severity="error" variant="outlined" sx={{ width: '100%' }}>
            Couldn't switch to the Platform membership: {error}
          </Alert>
        )}
      </Stack>
    </Box>
  )
}
